import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';

export const BOTTLE = {
  bodyRadius: 0.5,
  bodyHeight: 1.78,
  capRadius: 0.33,
  capHeight: 0.62,
  fillet: 0.13,
  /** Vertical centre of the whole bottle including the cap. */
  centerY: 1.1,
};

/** 2D profile of a cylinder with rounded top and bottom edges, for a lathe. */
export const roundedCylinder = (radius: number, height: number, fillet: number, steps = 10) => {
  const points: THREE.Vector2[] = [];
  const push = (x: number, y: number) => {
    const last = points[points.length - 1];
    if (last && Math.hypot(last.x - x, last.y - y) < 1e-4) return;
    points.push(new THREE.Vector2(x, y));
  };

  push(0, 0);
  push(Math.max(radius - fillet, 0), 0);

  for (let step = 1; step <= steps; step += 1) {
    const angle = -Math.PI / 2 + (Math.PI / 2) * (step / steps);
    push(radius - fillet + Math.cos(angle) * fillet, fillet + Math.sin(angle) * fillet);
  }

  push(radius, Math.max(height - fillet, fillet));

  for (let step = 1; step <= steps; step += 1) {
    const angle = (Math.PI / 2) * (step / steps);
    push(radius - fillet + Math.cos(angle) * fillet, height - fillet + Math.sin(angle) * fillet);
  }

  push(0, height);
  return points;
};

const GLASS_VERT = `
varying vec3 vNormal;
varying vec3 vViewDir;
void main() {
  vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
  vNormal = normalize(normalMatrix * normal);
  vViewDir = normalize(-viewPosition.xyz);
  gl_Position = projectionMatrix * viewPosition;
}
`;

const GLASS_FRAG = `
uniform float uInner;
varying vec3 vNormal;
varying vec3 vViewDir;
void main() {
  vec3 normal = normalize(vNormal);
  vec3 viewDir = normalize(vViewDir);
  float ndv = clamp(abs(dot(normal, viewDir)), 0.0, 1.0);
  float fresnel = pow(1.0 - ndv, 1.25);
  float rim = pow(1.0 - ndv, 3.4);
  float wall = smoothstep(0.18, 0.4, 1.0 - ndv) * (1.0 - smoothstep(0.42, 0.78, 1.0 - ndv));

  vec3 body = mix(vec3(0.68, 0.76, 0.82), vec3(0.46, 0.54, 0.60), uInner);
  vec3 dark = vec3(0.10, 0.12, 0.15);
  vec3 color = mix(body, dark, fresnel * mix(0.72, 0.9, uInner));
  color = mix(color, vec3(0.22, 0.28, 0.33), wall * 0.55);
  color += vec3(0.88, 0.94, 1.0) * rim * mix(0.55, 0.25, uInner);

  vec3 key = normalize(vec3(0.58, 0.74, 0.38));
  vec3 halfV = normalize(key + viewDir);
  float spec = pow(max(dot(normal, halfV), 0.0), 140.0);
  float glaze = pow(max(dot(normal, halfV), 0.0), 28.0) * 0.16;
  vec3 fill = normalize(vec3(-0.42, 0.52, 0.58));
  float specFill = pow(max(dot(normal, normalize(fill + viewDir)), 0.0), 70.0) * 0.32;
  color += vec3((spec + glaze + specFill) * mix(1.0, 0.35, uInner));

  float alpha = mix(mix(0.26, 0.9, fresnel), mix(0.12, 0.55, fresnel), uInner) + spec * 0.35;
  gl_FragColor = vec4(color, clamp(alpha, 0.1, 0.92));
}
`;

/** Clear empty glass bottle with a matte black cap. */
const Bottle: React.FC = () => {
  const shell = useMemo(
    () => roundedCylinder(BOTTLE.bodyRadius, BOTTLE.bodyHeight, BOTTLE.fillet),
    [],
  );
  const glass = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: { uInner: { value: 0 } },
        vertexShader: GLASS_VERT,
        fragmentShader: GLASS_FRAG,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    [],
  );
  const innerGlass = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: { uInner: { value: 1 } },
        vertexShader: GLASS_VERT,
        fragmentShader: GLASS_FRAG,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
        side: THREE.BackSide,
      }),
    [],
  );

  useEffect(
    () => () => {
      glass.dispose();
      innerGlass.dispose();
    },
    [glass, innerGlass],
  );

  return (
    <group>
      <mesh material={innerGlass} scale={[0.9, 0.93, 0.9]} position={[0, 0.05, 0]} renderOrder={1}>
        <latheGeometry args={[shell, 96]} />
      </mesh>
      <mesh castShadow material={glass} renderOrder={2}>
        <latheGeometry args={[shell, 128]} />
      </mesh>
      <mesh castShadow position={[0, BOTTLE.bodyHeight + BOTTLE.capHeight / 2 - 0.004, 0]}>
        <cylinderGeometry args={[BOTTLE.capRadius, BOTTLE.capRadius, BOTTLE.capHeight, 128]} />
        <meshStandardMaterial color="#141414" roughness={0.55} metalness={0.12} envMapIntensity={0.35} />
      </mesh>
    </group>
  );
};

export default Bottle;
